// src/components/vendor/VerificationRequired.tsx

import React from 'react';
import { Shield, CheckCircle, AlertCircle, Mail, User, Phone } from 'lucide-react';

interface VerificationRequiredProps {
    verificationStatus: {
        email: boolean;
        identity: boolean;
        phone: boolean;
    };
}

const VerificationRequired: React.FC<VerificationRequiredProps> = ({ verificationStatus }) => {
    return (
        <div className="max-w-2xl mx-auto p-6 space-y-8">
            {/* Header */}
            <div className="text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Shield className="h-8 w-8 text-amber-600" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900">Verification Required</h1>
                <p className="text-sm text-gray-500 mt-1">
                    You must complete all verification steps before applying to become a vendor.
                </p>
            </div>

            {/* Verification Checklist */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
                <h3 className="font-medium text-gray-900">Your Verification Status</h3>

                {/* Email Verification */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                        <Mail className="h-5 w-5 text-gray-400" />
                        <span className="text-sm text-gray-700">Email Verification</span>
                    </div>
                    {verificationStatus.email ? (
                        <span className="flex items-center gap-1.5 text-sm text-green-600">
                            <CheckCircle className="h-4 w-4" />
                            Verified
                        </span>
                    ) : (
                        <span className="flex items-center gap-1.5 text-sm text-amber-600">
                            <AlertCircle className="h-4 w-4" />
                            Pending
                        </span>
                    )}
                </div>

                {/* Identity Verification */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                        <User className="h-5 w-5 text-gray-400" />
                        <span className="text-sm text-gray-700">Identity Verification</span>
                    </div>
                    {verificationStatus.identity ? (
                        <span className="flex items-center gap-1.5 text-sm text-green-600">
                            <CheckCircle className="h-4 w-4" />
                            Verified
                        </span>
                    ) : (
                        <span className="flex items-center gap-1.5 text-sm text-amber-600">
                            <AlertCircle className="h-4 w-4" />
                            Pending
                        </span>
                    )}
                </div>

                {/* Phone Verification */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                        <Phone className="h-5 w-5 text-gray-400" />
                        <span className="text-sm text-gray-700">Phone Verification</span>
                    </div>
                    {verificationStatus.phone ? (
                        <span className="flex items-center gap-1.5 text-sm text-green-600">
                            <CheckCircle className="h-4 w-4" />
                            Verified
                        </span>
                    ) : (
                        <span className="flex items-center gap-1.5 text-sm text-amber-600">
                            <AlertCircle className="h-4 w-4" />
                            Pending
                        </span>
                    )}
                </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
                <a
                    href="/settings"
                    className="block w-full text-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                    Complete Verification
                </a>
                <a
                    href="/products"
                    className="block w-full text-center px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                    Back to Shopping
                </a>
            </div>

            {/* Help Text */}
            <p className="text-xs text-gray-400 text-center">
                Need help? Contact support at support@trincity.com
            </p>
        </div>
    );
};

export default VerificationRequired;